import { Link } from "@tanstack/react-router";
import { ArrowRight, ListFilter } from "lucide-react";
import { useMemo, useState } from "react";
import { type BarItem, BarList } from "@/components/charts/BarList";
import { DonutChart, type DonutSlice } from "@/components/charts/DonutChart";
import { DetailSection } from "@/components/shared/detail-section";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { StatusPill, type StatusTone } from "@/components/shared/status-badge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EntityCombobox } from "@/components/ui/entity-combobox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  type CompetencyNegativeCount,
  type CycleFeedbackFilters,
  type CycleFeedbackRow,
  type SentimentCount,
  type TriageStatus,
  useCycleFeedback,
  useCycleFeedbackThemes,
  useSetFeedbackTriage,
} from "@/hooks/use-cycle-feedback";
import { useReviewConfig } from "@/hooks/use-review-config";
import { useReviewCycles } from "@/hooks/use-review-cycles";
import { useFeedbackSentiments } from "@/hooks/use-review-feedback";
import { useReviewRelationshipTypes } from "@/hooks/use-review-templates";
import { categoryColorVar } from "@/lib/category-colors";
import { formatDateOnly } from "@/lib/dates";
import { cn } from "@/lib/utils";

const CHART_COLORS = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
];

const TRIAGE_LABELS: Record<TriageStatus, string> = {
  unreviewed: "Unreviewed",
  reviewed: "Reviewed",
  needs_follow_up: "Needs follow-up",
};

const TRIAGE_ORDER: TriageStatus[] = ["unreviewed", "reviewed", "needs_follow_up"];

/**
 * `review_sentiment` carries no polarity column of its own — same flagged
 * simplification as `NEGATIVE_SENTIMENT_KEYS` in `review-feedback-cycle.ts`,
 * applied here to color rather than to "most negative" math. Keyed to the
 * same seeded list; an unrecognized key (a future sentiment HR adds) falls
 * back to neutral rather than guessing a tone for it.
 */
const SENTIMENT_TONE: Record<string, StatusTone> = {
  strength: "success",
  development: "warning",
  observation: "neutral",
};

// Same semantic tokens `StatusPill` uses, applied to the ACTIVE state only —
// the other two stay plain outline buttons, so the one true state reads at a
// glance rather than three equally-loud options competing for attention.
const TRIAGE_ACTIVE_CLASS: Record<TriageStatus, string> = {
  unreviewed: "border-transparent bg-muted text-muted-foreground hover:bg-muted",
  reviewed: "border-transparent bg-success-bg text-success-foreground hover:bg-success-bg",
  needs_follow_up: "border-transparent bg-warning-bg text-warning-foreground hover:bg-warning-bg",
};

// A touch more weight than the app's default card (`shadow-sm`), so each
// panel on this report reads as its own block at a glance — there are four
// of them stacked on one page, more than most detail screens carry at once.
const PANEL_CLASS = "shadow-md";

/**
 * HR's cycle-wide feedback triage — DM-WF-001, EPIC-072 (WI-270/271/272/273).
 *
 * One round at a time, HR/admin-only (same `requireViewAll` tier as the
 * competency and recognition reports). Saving/sharing a filter set (WI-274)
 * is deliberately not here — see the companion build spec.
 */
export function CycleFeedbackReport() {
  const [cycleId, setCycleId] = useState<string | null>(null);
  const [filters, setFilters] = useState<CycleFeedbackFilters>({});
  const { data: cycles = [] } = useReviewCycles();

  function setFilter<K extends keyof CycleFeedbackFilters>(key: K, value: CycleFeedbackFilters[K]) {
    setFilters((f) => ({ ...f, [key]: value }));
  }

  return (
    <div className="space-y-6">
      <DetailSection title="Choose a round" boldTitle className={PANEL_CLASS}>
        <Select
          value={cycleId ?? undefined}
          onValueChange={(id) => {
            setCycleId(id);
            setFilters({});
          }}
        >
          <SelectTrigger className="w-full sm:w-96" id="cycle-feedback-cycle">
            <SelectValue placeholder="Choose a round…" />
          </SelectTrigger>
          <SelectContent>
            {cycles.map((cycle) => (
              <SelectItem key={cycle.id} value={cycle.id}>
                {cycle.name}
                {cycle.statusKey !== "closed" ? " (still open)" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </DetailSection>

      {cycleId && (
        <>
          <ThemesSection
            cycleId={cycleId}
            onDrillInto={(patch) => setFilters((f) => ({ ...f, ...patch }))}
          />
          <FiltersSection
            filters={filters}
            onSetFilter={setFilter}
            onClearFilters={() => setFilters({})}
          />
          <FeedbackSection cycleId={cycleId} filters={filters} />
        </>
      )}
    </div>
  );
}

function ThemesSection({
  cycleId,
  onDrillInto,
}: {
  cycleId: string;
  onDrillInto: (patch: CycleFeedbackFilters) => void;
}) {
  const { data, isLoading, isError, error, refetch } = useCycleFeedbackThemes(cycleId);

  const overallSlices: DonutSlice[] = useMemo(
    () =>
      (data?.overall ?? []).map((s: SentimentCount, i) => ({
        label: s.sentimentName,
        value: s.count,
        color: CHART_COLORS[i % CHART_COLORS.length] ?? "var(--chart-1)",
      })),
    [data?.overall],
  );

  const negativeItems: BarItem[] = useMemo(
    () =>
      (data?.negativeByCompetency ?? []).slice(0, 8).map((c: CompetencyNegativeCount) => ({
        label: c.competencyName,
        value: c.negativeCount,
        color: categoryColorVar(c.categoryColor) ?? "var(--danger)",
      })),
    [data?.negativeByCompetency],
  );

  return (
    <DetailSection title="Themes at a glance" boldTitle className={PANEL_CLASS}>
      {isError ? (
        <ErrorState
          message={error instanceof Error ? error.message : "Couldn't load this round's themes"}
          onRetry={() => refetch()}
        />
      ) : isLoading ? (
        <Skeleton className="h-56 w-full" />
      ) : !data || data.overall.length === 0 ? (
        <EmptyState
          title="Nothing to show yet"
          description="No written feedback has been submitted in this round."
        />
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-muted-foreground">
              Sentiment distribution
            </h3>
            <DonutChart
              title="Sentiment distribution across the round"
              slices={overallSlices}
              centerLabel={overallSlices.reduce((n, s) => n + s.value, 0) === 1 ? "note" : "notes"}
            />
          </div>
          <div className="md:border-l md:pl-6">
            <h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-muted-foreground">
              Competencies attracting the most negative feedback
            </h3>
            {negativeItems.length > 0 ? (
              <BarList
                title="Competencies attracting the most negative feedback"
                items={negativeItems}
              />
            ) : (
              <p className="text-sm text-muted-foreground">
                No negative feedback is tagged to a competency yet.
              </p>
            )}
            {(data.negativeByCompetency ?? []).length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {data.negativeByCompetency.slice(0, 8).map((c) => (
                  <Button
                    key={c.competencyId}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => onDrillInto({ competencyId: c.competencyId })}
                  >
                    {c.competencyName}
                    <ArrowRight className="size-3.5" aria-hidden />
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </DetailSection>
  );
}

function FiltersSection({
  filters,
  onSetFilter,
  onClearFilters,
}: {
  filters: CycleFeedbackFilters;
  onSetFilter: <K extends keyof CycleFeedbackFilters>(
    key: K,
    value: CycleFeedbackFilters[K],
  ) => void;
  onClearFilters: () => void;
}) {
  const { data: sentiments = [] } = useFeedbackSentiments();
  const { data: relationships = [] } = useReviewRelationshipTypes();
  const { data: statuses = [] } = useReviewConfig("statuses");
  const hasFilters = Object.values(filters).some((v) => v !== undefined);

  return (
    <DetailSection
      title="Filter feedback"
      boldTitle
      className={`${PANEL_CLASS} border-dashed`}
      headerActions={
        hasFilters ? (
          <Button type="button" variant="outline" size="sm" onClick={onClearFilters}>
            Clear filters
          </Button>
        ) : undefined
      }
    >
      <div className="flex items-center gap-2 pb-4 text-muted-foreground text-sm">
        <ListFilter className="size-4" aria-hidden />
        Narrow the list below. Everything here combines — pick several and only notes matching all
        of them show.
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-7">
        <FilterSelect
          label="Sentiment"
          value={filters.sentimentKey}
          onChange={(v) => onSetFilter("sentimentKey", v)}
          options={sentiments.map((s) => ({ value: s.key, label: s.name }))}
        />
        <div>
          <span className="mb-1 block text-muted-foreground text-xs">Competency</span>
          <EntityCombobox
            entity="competencies"
            value={filters.competencyId ?? null}
            onChange={(v) => onSetFilter("competencyId", v ?? undefined)}
            includeNone
            placeholder="Any competency"
          />
        </div>
        <div>
          <span className="mb-1 block text-muted-foreground text-xs">Role</span>
          <EntityCombobox
            entity="roles"
            value={filters.roleId ?? null}
            onChange={(v) => onSetFilter("roleId", v ?? undefined)}
            includeNone
            placeholder="Any role"
          />
        </div>
        <div>
          <span className="mb-1 block text-muted-foreground text-xs">Level</span>
          <EntityCombobox
            entity="role-levels"
            value={filters.roleLevelId ?? null}
            onChange={(v) => onSetFilter("roleLevelId", v ?? undefined)}
            includeNone
            placeholder="Any level"
          />
        </div>
        <FilterSelect
          label="Relationship"
          value={filters.relationshipKey}
          onChange={(v) => onSetFilter("relationshipKey", v)}
          options={relationships.map((r) => ({ value: r.key, label: r.name }))}
        />
        <FilterSelect
          label="Review status"
          value={filters.reviewStatusKey}
          onChange={(v) => onSetFilter("reviewStatusKey", v)}
          options={statuses.map((s) => ({ value: s.key, label: s.name }))}
        />
        <FilterSelect
          label="Triage"
          value={filters.triageStatus}
          onChange={(v) => onSetFilter("triageStatus", v as TriageStatus | undefined)}
          options={(["unreviewed", "reviewed", "needs_follow_up"] as const).map((k) => ({
            value: k,
            label: TRIAGE_LABELS[k],
          }))}
        />
      </div>
    </DetailSection>
  );
}

function FeedbackSection({ cycleId, filters }: { cycleId: string; filters: CycleFeedbackFilters }) {
  const { data, isLoading, isError, error, refetch } = useCycleFeedback(cycleId, filters);
  const triage = useSetFeedbackTriage();
  const hasFilters = Object.values(filters).some((v) => v !== undefined);

  return (
    <DetailSection
      title="Feedback"
      boldTitle
      className={PANEL_CLASS}
      headerActions={
        <Badge variant="secondary" className="font-normal">
          {isLoading ? "…" : `${data?.length ?? 0} ${data?.length === 1 ? "note" : "notes"}`}
        </Badge>
      }
    >
      {isError ? (
        <ErrorState
          message={error instanceof Error ? error.message : "Couldn't load feedback for this round"}
          onRetry={() => refetch()}
        />
      ) : isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : !data || data.length === 0 ? (
        <EmptyState
          title={hasFilters ? "Nothing matches those filters" : "No feedback yet"}
          description={
            hasFilters ? "Try clearing a filter." : "Nobody has written feedback in this round yet."
          }
        />
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {data.map((row: CycleFeedbackRow) => (
            <li key={row.id} className="space-y-1.5 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-1.5">
                  <Link
                    to="/dashboard/persons/$personId"
                    params={{ personId: row.subjectPersonId }}
                    className="font-medium text-sm hover:underline"
                  >
                    {row.subjectName}
                  </Link>
                  <StatusPill
                    tone={SENTIMENT_TONE[row.sentimentKey] ?? "neutral"}
                    label={row.sentimentName}
                  />
                  {row.criterionTitle &&
                    (() => {
                      const dotColor = categoryColorVar(row.categoryColor);
                      return (
                        <Badge variant="outline" className="gap-1.5 font-normal">
                          {dotColor && (
                            <span
                              className="size-1.5 shrink-0 rounded-full"
                              style={{ backgroundColor: dotColor }}
                              aria-hidden
                            />
                          )}
                          {row.criterionTitle}
                        </Badge>
                      );
                    })()}
                </div>
                <span className="text-muted-foreground text-xs">
                  {row.isAnonymous ? "Anonymous" : (row.relationshipName ?? "Pooled")} ·{" "}
                  {formatDateOnly(row.createdAt)}
                </span>
              </div>
              <p className="text-sm">{row.body}</p>
              <TriageToggle
                status={row.triageStatus}
                pending={triage.isPending && triage.variables?.feedbackId === row.id}
                onChange={(status) => triage.mutate({ feedbackId: row.id, status })}
              />
            </li>
          ))}
        </ul>
      )}
    </DetailSection>
  );
}

/**
 * Three real buttons rather than a dropdown — this is the action HR takes on
 * every single row, so it earns the same "look like a button" treatment the
 * drill-through links just got, not a tiny `<select>` easy to skim past. The
 * current state is color-coded with the app's own status tokens; the other
 * two stay plain outline so the one true state doesn't have to compete with
 * two equally-loud alternatives.
 */
function TriageToggle({
  status,
  pending,
  onChange,
}: {
  status: TriageStatus;
  pending: boolean;
  onChange: (status: TriageStatus) => void;
}) {
  return (
    <fieldset className="m-0 flex flex-wrap items-center gap-2 border-0 border-border border-t p-0 pt-2">
      <legend className="sr-only">Triage status</legend>
      <span className="text-muted-foreground text-xs font-medium">Triage</span>
      <div className="flex gap-1.5">
        {TRIAGE_ORDER.map((s) => {
          const active = status === s;
          return (
            <Button
              key={s}
              type="button"
              size="sm"
              variant="outline"
              aria-pressed={active}
              disabled={pending}
              onClick={() => onChange(s)}
              className={cn("h-7 rounded-full px-3 text-xs", active && TRIAGE_ACTIVE_CLASS[s])}
            >
              {TRIAGE_LABELS[s]}
            </Button>
          );
        })}
      </div>
    </fieldset>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div>
      <span className="mb-1 block text-muted-foreground text-xs">{label}</span>
      <Select
        value={value ?? "__all"}
        onValueChange={(v) => onChange(v === "__all" ? undefined : v)}
      >
        <SelectTrigger className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all">Any</SelectItem>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
